// shop-frontend/src/pages/ShopProductViews.jsx
import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { toAbsUrl, getShopAnalyticsSummary, getShopRevenueTrend, getShopViewsSummary } from "../utils/api";
import { getDisplayImage } from "../utils/productImage";

/* ─── tiny helpers ─────────────────────────────────────────────────────── */
function fmt(n, decimals = 2) {
  return Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}
function fmtInt(n) { return Number(n || 0).toLocaleString("en-US"); }
function fmtDate(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}
function growth(val) {
  const n = Number(val || 0);
  if (!n) return null;
  const sign = n > 0 ? "+" : "";
  const color = n > 0 ? "text-emerald-400" : "text-red-400";
  return <span className={`text-xs font-bold ${color}`}>{sign}{n.toFixed(1)}%</span>;
}

/* ─── Bar chart (pure CSS, no library) ─────────────────────────────────── */
function RevenueChart({ data }) {
  if (!data?.length) return null;
  const max = Math.max(...data.map((d) => d.revenue), 0.01);
  return (
    <div className="flex items-end gap-1 h-24 w-full">
      {data.map((d, i) => {
        const pct = Math.max(2, (d.revenue / max) * 100);
        return (
          <div key={i} className="flex flex-col items-center flex-1 gap-1 group relative">
            <div
              className="w-full rounded-t-sm bg-blue-500/70 group-hover:bg-blue-400 transition-all"
              style={{ height: `${pct}%` }}
            />
            {/* tooltip */}
            <div className="absolute bottom-full mb-1 hidden group-hover:block bg-gray-900 text-white text-[10px] px-2 py-1 rounded whitespace-nowrap z-10 pointer-events-none">
              {fmtDate(d.date)}: ${fmt(d.revenue)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ─── Stat card ─────────────────────────────────────────────────────────── */
function StatCard({ label, value, sub, accent }) {
  return (
    <div className="lsa-root lsa-card rounded-xl border p-5 space-y-1">
      <div className="lsa-text-muted text-xs font-semibold uppercase tracking-wide">{label}</div>
      <div className={`text-2xl font-black lsa-text-primary ${accent || ""}`}>{value}</div>
      {sub && <div className="lsa-text-muted text-xs">{sub}</div>}
    </div>
  );
}

/* ─── Main page ─────────────────────────────────────────────────────────── */
export default function ShopProductViews() {
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [loadingViews, setLoadingViews]     = useState(true);
  const [loadingTrend, setLoadingTrend]     = useState(true);

  const [summary, setSummary]     = useState(null);
  const [views, setViews]         = useState(null);
  const [trend, setTrend]         = useState([]);
  const [trendPeriod, setTrendPeriod] = useState("7days");
  const [error, setError]         = useState("");

  // Load analytics summary
  const loadSummary = useCallback(async () => {
    setLoadingSummary(true);
    try {
      const data = await getShopAnalyticsSummary();
      setSummary(data);
    } catch { /* non-fatal */ }
    finally { setLoadingSummary(false); }
  }, []);

  // Load views (top products)
  const loadViews = useCallback(async () => {
    setLoadingViews(true);
    try {
      const data = await getShopViewsSummary();
      setViews(data);
    } catch (e) {
      setError(e?.message || "Failed to load analytics");
    } finally { setLoadingViews(false); }
  }, []);

  // Load revenue trend
  const loadTrend = useCallback(async (period) => {
    setLoadingTrend(true);
    try {
      const data = await getShopRevenueTrend(period);
      setTrend(Array.isArray(data?.data) ? data.data : []);
    } catch { setTrend([]); }
    finally { setLoadingTrend(false); }
  }, []);

  useEffect(() => {
    loadSummary();
    loadViews();
  }, [loadSummary, loadViews]);

  useEffect(() => { loadTrend(trendPeriod); }, [trendPeriod, loadTrend]);

  const topProducts = Array.isArray(views?.top) ? views.top
    : Array.isArray(views?.products) ? views.products : [];

  return (
    <div className="page-responsive max-w-5xl mx-auto min-h-screen space-y-6" style={{ background: "var(--bg-primary)" }}>

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-black lsa-text-primary">Analytics</h1>
          <p className="lsa-text-muted text-sm mt-0.5">Revenue, orders, and product performance</p>
        </div>
        <button
          onClick={() => { loadSummary(); loadViews(); loadTrend(trendPeriod); }}
          className="px-4 py-2 rounded-lg text-sm font-semibold lsa-btn-ghost lsa-text-muted border transition-colors"
          style={{ borderColor: "var(--lsa-border)" }}
        >
          Refresh
        </button>
      </div>

      {error && (
        <div className="rounded-xl p-4 text-sm font-semibold text-red-400 border border-red-500/30 bg-red-500/10">
          {error}
        </div>
      )}

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {loadingSummary ? (
          [1,2,3,4].map((i) => (
            <div key={i} className="lsa-root lsa-card rounded-xl border p-5 animate-pulse h-24" />
          ))
        ) : (
          <>
            <StatCard
              label="Today's Revenue"
              value={`$${fmt(summary?.todayRevenue)}`}
              sub={growth(summary?.revenueGrowth) ? <span>vs yesterday {growth(summary?.revenueGrowth)}</span> : "vs yesterday"}
            />
            <StatCard label="Today's Orders" value={fmtInt(summary?.todayOrders)} sub="Paid orders today" />
            <StatCard label="This Week" value={`$${fmt(summary?.weekRevenue)}`} sub={`${fmtInt(summary?.weekOrders)} orders`} />
            <StatCard label="This Month" value={`$${fmt(summary?.monthRevenue)}`} sub={`${fmtInt(summary?.monthOrders)} orders`} />
          </>
        )}
      </div>

      {/* Secondary stats row */}
      {!loadingSummary && summary && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <StatCard label="Pending Orders" value={fmtInt(summary.pendingOrders)} sub="Awaiting fulfillment" />
          <StatCard label="Total Views" value={fmtInt(views?.totalViews)} sub="Across all products" />
          <StatCard label="Low Stock" value={fmtInt(summary.lowStockProducts)} sub="Products below threshold"
            accent={summary.lowStockProducts > 0 ? "text-orange-400" : ""} />
        </div>
      )}

      {/* Revenue Trend Chart */}
      <div className="lsa-root lsa-card rounded-xl border p-5 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h3 className="font-bold lsa-text-primary">Revenue Trend</h3>
          <div className="flex gap-2">
            {["7days", "30days"].map((p) => (
              <button
                key={p}
                onClick={() => setTrendPeriod(p)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                  trendPeriod === p
                    ? "bg-blue-600 text-white"
                    : "lsa-text-muted lsa-btn-ghost border"
                }`}
                style={trendPeriod !== p ? { borderColor: "var(--lsa-border)" } : {}}
              >
                {p === "7days" ? "7 Days" : "30 Days"}
              </button>
            ))}
          </div>
        </div>

        {loadingTrend ? (
          <div className="h-24 rounded-lg animate-pulse" style={{ background: "var(--lsa-row-hover)" }} />
        ) : trend.length === 0 ? (
          <div className="h-24 flex items-center justify-center lsa-text-muted text-sm">No revenue data yet.</div>
        ) : (
          <>
            <RevenueChart data={trend} />
            <div className="flex justify-between lsa-text-muted text-[10px] px-1">
              <span>{fmtDate(trend[0]?.date)}</span>
              <span>{fmtDate(trend[trend.length - 1]?.date)}</span>
            </div>
          </>
        )}
      </div>

      {/* Top Viewed Products */}
      <div className="lsa-root lsa-card rounded-xl border overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 lsa-border-b">
          <h3 className="font-bold lsa-text-primary">Top Viewed Products</h3>
          {loadingViews && <span className="lsa-text-muted text-xs animate-pulse">Updating…</span>}
        </div>

        {loadingViews && topProducts.length === 0 ? (
          <div className="p-12 text-center lsa-text-muted text-sm">Loading…</div>
        ) : topProducts.length === 0 ? (
          <div className="p-12 text-center lsa-text-muted text-sm">No view data yet.</div>
        ) : (
          <div>
            {topProducts.map((p, i) => {
              const img = getDisplayImage(p);
              return (
                <div key={p._id || i} className="flex items-center gap-4 px-5 py-4 lsa-border-b lsa-row-hover transition-colors last:border-0">
                  <span className="w-7 text-sm lsa-text-muted font-semibold">#{i + 1}</span>
                  <div className="w-12 h-12 rounded-lg flex-shrink-0 overflow-hidden lsa-img-bg">
                    {img && <img src={toAbsUrl(img)} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <Link
                      to={`/shop/product/${p._id}`}
                      className="text-sm font-semibold lsa-text-primary hover:text-blue-400 transition-colors block truncate"
                    >
                      {p.title || "Untitled"}
                    </Link>
                    {p.category && <div className="lsa-text-muted text-xs mt-0.5">{p.category}</div>}
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-sm font-black lsa-text-primary">{fmtInt(p.views)}</div>
                    <div className="lsa-text-muted text-[10px] uppercase tracking-wide">Views</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
